import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Check, RotateCw, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useApiBase } from "../context/ApiBaseContext";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";

/**
 * Iter-104 — QR-code project invite dialog.
 *
 * Renders a high-contrast QR encoding the same `/join/{code}` URL the
 * "Copy link" button surfaces. On set, an HOD/admin can show this on
 * their phone, a department member scans it with the OS camera, lands
 * on the join page, signs in (or registers), and joins.
 *
 * Owners/admins also get a Regenerate button — invalidates the old
 * code immediately so a leaked screenshot / QR can be rotated.
 */
export default function ProjectInviteQRDialog({
    open,
    onOpenChange,
    projectId,
    projectName,
    inviteCode,
    inviteLink,
    canRegenerate,
    onRegenerated,
}) {
    const apiBase = useApiBase();
    const [copied, setCopied] = useState(false);
    const [regenerating, setRegenerating] = useState(false);

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(inviteLink);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {
            toast.error("Could not copy link");
        }
    };

    const regenerate = async () => {
        if (!canRegenerate) return;
        const ok = window.confirm(
            "Regenerate the invite code?\n\nThe current QR code and any links you've already shared will stop working immediately. Existing members keep their access.",
        );
        if (!ok) return;
        setRegenerating(true);
        try {
            const { data } = await api.post(
                `${apiBase}/${projectId}/invite-code/regenerate`,
            );
            onRegenerated?.(data);
            toast.success("Invite code regenerated");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setRegenerating(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="project-invite-qr-dialog"
                className="max-w-md bg-card border-white/10"
            >
                <DialogHeader className="pr-8">
                    <DialogTitle className="font-display text-xl font-bold text-white">
                        Scan to join
                    </DialogTitle>
                    <DialogDescription className="text-neutral-400">
                        Anyone scanning this QR code can join{" "}
                        <span className="font-semibold text-neutral-200">
                            {projectName || "this project"}
                        </span>{" "}
                        after they sign in.
                    </DialogDescription>
                </DialogHeader>

                <div className="mt-3 flex flex-col items-center gap-3">
                    {/* High-contrast QR — white background + dark fg so
                        old camera apps with poor low-light handling
                        still parse it. Container has a thick padding
                        ring as a "quiet zone" the QR spec requires for
                        reliable scanning. */}
                    <div
                        data-testid="project-invite-qr-canvas"
                        className="rounded-2xl bg-white p-5 shadow-lg shadow-black/30"
                    >
                        {inviteLink ? (
                            <QRCodeSVG
                                value={inviteLink}
                                size={224}
                                level="M"
                                marginSize={0}
                                bgColor="#ffffff"
                                fgColor="#0c0a18"
                            />
                        ) : (
                            <div className="h-[224px] w-[224px] flex items-center justify-center text-neutral-500 text-sm">
                                <Loader2 size={18} className="animate-spin" />
                            </div>
                        )}
                    </div>

                    <div className="w-full text-center">
                        <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                            Invite code
                        </div>
                        <div
                            data-testid="project-invite-qr-code"
                            className="mono text-2xl font-semibold text-white tracking-[0.3em] mt-1"
                        >
                            {inviteCode || "------"}
                        </div>
                    </div>

                    <p
                        data-testid="project-invite-qr-link"
                        className="mono text-[11px] text-neutral-500 break-all w-full text-center"
                    >
                        {inviteLink}
                    </p>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-2">
                    <Button
                        type="button"
                        data-testid="project-invite-qr-copy"
                        variant="secondary"
                        onClick={copyLink}
                        disabled={!inviteLink}
                        className="min-tap"
                    >
                        {copied ? (
                            <>
                                <Check size={14} className="mr-2" />
                                Copied
                            </>
                        ) : (
                            <>
                                <Copy size={14} className="mr-2" />
                                Copy link
                            </>
                        )}
                    </Button>
                    <Button
                        type="button"
                        data-testid="project-invite-qr-close"
                        onClick={() => onOpenChange(false)}
                        className="min-tap"
                    >
                        <X size={14} className="mr-2" />
                        Close
                    </Button>
                </div>

                {canRegenerate ? (
                    <button
                        type="button"
                        data-testid="project-invite-qr-regenerate"
                        onClick={regenerate}
                        disabled={regenerating}
                        className="tap mt-3 w-full inline-flex items-center justify-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-neutral-400 hover:text-amber-300 transition-colors disabled:opacity-60"
                    >
                        {regenerating ? (
                            <Loader2 size={11} className="animate-spin" />
                        ) : (
                            <RotateCw size={11} />
                        )}
                        Regenerate code
                    </button>
                ) : null}
            </DialogContent>
        </Dialog>
    );
}
